import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { fmtCurrency, fmtDate } from "@/lib/format";
import { PageHeader, Panel } from "@/components/common/ui-kit";
import { Input } from "@/components/ui/input";
import { PeriodPicker, usePeriod } from "@/components/billing/PeriodPicker";

const Row = ({ s, total }) => {
  const share = total ? (s.net / total) * 100 : 0;
  const name = s.company_id ? <Link to={`/fornitori/${s.company_id}`} className="font-semibold text-ink hover:text-brand-600" data-testid={`supplier-link-${s.company_id}`}>{s.name}</Link> : <span className="font-semibold text-ink">{s.name}</span>;
  return (
    <tr className="border-b border-line/60 last:border-0" data-testid="supplier-row">
      <td className="py-2.5 pr-3">{name}<p className="text-xs text-slate-500">{[s.vat && `P.IVA ${s.vat}`, s.city, s.email, s.phone].filter(Boolean).join(" · ") || "\u2014"}</p>
        {s.categories.length > 0 && <p className="text-[11px] text-slate-400">{s.categories.join(", ")}</p>}</td>
      <td className="py-2.5 pr-3 text-right tabular-nums">{s.count}</td>
      <td className="py-2.5 pr-3 text-right font-semibold tabular-nums">{fmtCurrency(s.net)}
        <div className="ml-auto mt-1 h-1 w-24 rounded bg-slate-100"><div className="h-1 rounded bg-brand-500" style={{ width: `${Math.min(100, share)}%` }} /></div></td>
      <td className="py-2.5 pr-3 text-right tabular-nums">{fmtCurrency(s.matched_net)}</td>
      <td className={`py-2.5 pr-3 text-right tabular-nums ${s.to_match ? "text-amber-700" : "text-slate-400"}`}>{s.to_match}</td>
      <td className="py-2.5 text-right text-xs text-slate-600">{fmtDate(s.last_date)}</td>
    </tr>
  );
};

export default function SuppliersPage() {
  const per = usePeriod("year");
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);
  useEffect(() => {
    const t = setTimeout(() => api.get("/supplier-invoices/suppliers", { params: { ...JSON.parse(per.rangeKey), ...(q.trim() ? { q: q.trim() } : {}) } })
      .then((r) => setData(r.data)).catch((e) => toast.error(errorMessage(e))), 250);
    return () => clearTimeout(t);
  }, [per.rangeKey, q]);
  return (
    <div data-testid="suppliers-page">
      <PageHeader overline="Amministrazione · ciclo passivo" title="Fornitori"
        description="Fornitori ricavati dalle fatture ricevute in Fatture in Cloud: quanto acquisti da ciascuno, quanto è già abbinato agli ordini e cosa resta da abbinare." />
      <Panel className="p-4 md:p-5">
        <div className="flex flex-wrap items-start gap-2"><PeriodPicker {...per} testId="suppliers" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca fornitore o P.IVA" className="ml-auto h-9 w-60" data-testid="suppliers-search" /></div>
        {data && <p className="mb-3 text-xs text-slate-500" data-testid="suppliers-summary"><span className="capitalize">{per.range.label}</span> · {data.summary.suppliers} fornitori · {data.summary.invoices} fatture · {fmtCurrency(data.summary.net)} imponibile</p>}
        <div className="overflow-x-auto"><table className="w-full text-sm" data-testid="suppliers-table">
          <thead><tr className="border-b border-line text-left text-[11px] uppercase tracking-wide text-slate-500">
            <th className="py-2 pr-3 font-medium">Fornitore</th><th className="py-2 pr-3 text-right font-medium">Fatture</th><th className="py-2 pr-3 text-right font-medium">Imponibile</th>
            <th className="py-2 pr-3 text-right font-medium">Abbinato a ordini</th><th className="py-2 pr-3 text-right font-medium">Da abbinare</th><th className="py-2 text-right font-medium">Ultima fattura</th></tr></thead>
          <tbody>{(data?.items || []).map((s) => <Row key={s.company_id || s.name} s={s} total={data.summary.net} />)}</tbody></table></div>
        {data && !data.items.length && <p className="py-8 text-center text-sm text-slate-500" data-testid="suppliers-empty">Nessun fornitore nel periodo. Le fatture si importano da «Fatture fornitori».</p>}
      </Panel>
    </div>
  );
}
